import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { getMe } from "./api-modules/me.js";
import type { HearthUser } from "./api-modules/types.js";

/**
 * Auth context for the Hearth SPA. Wraps Amplify v6 + a TanStack Query
 * for /v1/me as the canonical user-profile source.
 *
 * State machine:
 *   loading — initial mount; we're checking fetchAuthSession to see if
 *             a stored refresh token exists.
 *   unauth  — no valid session; render sign-in/sign-up routes.
 *   auth    — Amplify says signed-in AND /v1/me succeeded; render the
 *             app. The HearthUser is the live profile.
 *
 * **Why fetchAuthSession on mount instead of relying on Hub events:**
 * Amplify v6 issue #12739 — the `signedIn` Hub event doesn't always
 * fire on page-load resume from cookies/localStorage. fetchAuthSession
 * is the source-of-truth check. We then subscribe to Hub for *changes*
 * after the initial check (sign-out, token-refresh failure).
 */

type AuthStatus = "loading" | "unauth" | "auth";

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: HearthUser | null;
  readonly error: Error | null;
  readonly refresh: () => Promise<void>;
  readonly signOut: (opts?: { global?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // hasSession: did fetchAuthSession find a valid refresh token? Drives
  // whether we attempt /v1/me at all.
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  const queryClient = useQueryClient();

  const checkSession = useCallback(async () => {
    try {
      let session = await fetchAuthSession();
      let hasToken = Boolean(session.tokens?.accessToken);
      // Amplify v6 issue #12778: fetchAuthSession occasionally returns
      // `tokens: undefined` immediately after confirmSignIn even though
      // the session is established. A short delay + forceRefresh
      // recovers reliably. Without this, the post-MFA-setup navigation
      // race lands the user back on /signin with Amplify reporting
      // "There is already a signed in user" on the next signIn attempt.
      if (!hasToken) {
        await new Promise((r) => setTimeout(r, 250));
        session = await fetchAuthSession({ forceRefresh: true });
        hasToken = Boolean(session.tokens?.accessToken);
      }
      setHasSession(hasToken);
    } catch {
      setHasSession(false);
    }
  }, []);

  // Initial session check on mount.
  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  // Hub listener for auth-state changes initiated elsewhere (sign-out,
  // token-refresh failure, sign-in via SignInForm).
  useEffect(() => {
    const stop = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          setHasSession(true);
          // Re-fetch /v1/me to pick up post-confirmation row writes.
          void queryClient.invalidateQueries({ queryKey: ["me"] });
          break;
        case "signedOut":
        case "tokenRefresh_failure":
          setHasSession(false);
          queryClient.clear();
          break;
        case "tokenRefresh":
          // Tokens rotated; existing /v1/me cache is still valid.
          break;
        default:
          break;
      }
    });
    return () => stop();
  }, [queryClient]);

  const meQuery = useQuery<HearthUser, Error>({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: hasSession === true,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const status: AuthStatus = useMemo(() => {
    if (hasSession === null) return "loading";
    if (hasSession === false) return "unauth";
    if (meQuery.isPending || meQuery.isFetching) return "loading";
    if (meQuery.isError) return "unauth";
    if (meQuery.data) return "auth";
    return "loading";
  }, [hasSession, meQuery.data, meQuery.isError, meQuery.isFetching, meQuery.isPending]);

  const refresh = useCallback(async () => {
    await checkSession();
    await queryClient.invalidateQueries({ queryKey: ["me"] });
  }, [checkSession, queryClient]);

  const signOut = useCallback(
    async (opts?: { global?: boolean }) => {
      await amplifySignOut(opts?.global ? { global: true } : undefined);
      setHasSession(false);
      queryClient.clear();
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: meQuery.data ?? null,
      error: meQuery.error ?? null,
      refresh,
      signOut,
    }),
    [status, meQuery.data, meQuery.error, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be called inside AuthProvider");
  }
  return ctx;
}
